import { UserLanguage } from '../../../../features/account/types/User'
import { LanguageMaps } from '../../../api/language'
import { ContentLanguageTranslation } from '../../../types/InternalAnalystComment'
import {
  ConventionalItemMutation,
  ConventionalItemType,
  ConventionalTranslatedField,
  ConventionalTranslations,
  InternalConventionalItem,
  SavedConventionalItem,
} from '../types/InternalConventionalItem'

export const conventionalItemTypes: ConventionalItemType[] = [ConventionalItemType.Category, ConventionalItemType.Genre, ConventionalItemType.Subgenre]

export const conventionalLanguages: UserLanguage[] = ['en', 'ja', 'zh']

export const maxExampleGames = 50

// The language-map group and the translated fields of each type, as legacy's `prepareForSave`.
export const conventionalTypeConfig: Record<ConventionalItemType, { group: string; fields: ConventionalTranslatedField[] }> = {
  [ConventionalItemType.Category]: { group: 'conventionalCategories', fields: ['name'] },
  [ConventionalItemType.Genre]: { group: 'conventionalGenres', fields: ['name', 'description'] },
  [ConventionalItemType.Subgenre]: { group: 'conventionalSubgenres', fields: ['name', 'description', 'imageText'] },
}

export const conventionalTypeLabelKeys: Record<ConventionalItemType, string> = {
  [ConventionalItemType.Category]: 'internal-service-management:conventional_genres_type_category',
  [ConventionalItemType.Genre]: 'internal-service-management:conventional_genres_type_genre',
  [ConventionalItemType.Subgenre]: 'internal-service-management:conventional_genres_type_subgenre',
}

export type ConventionalItemRow = {
  type: ConventionalItemType
  item: InternalConventionalItem
}

export type ConventionalItemFormValues = {
  names: ContentLanguageTranslation
  descriptions: ContentLanguageTranslation
  imageTexts: ContentLanguageTranslation
  // '' when there is no image.
  imageUrl: string
  exampleAppIds: number[]
}

const compareCodeUnits = (a: string, b: string) => (a < b ? -1 : a > b ? 1 : 0)

// Categories, then genres, then subgenres, each by name, as legacy's `_.union` of three `_.sortBy(name)` lists.
export const buildConventionalRows = (items: Partial<Record<ConventionalItemType, InternalConventionalItem[]>>): ConventionalItemRow[] =>
  conventionalItemTypes.flatMap((type) => [...(items[type] ?? [])].sort((a, b) => compareCodeUnits(a.name ?? '', b.name ?? '')).map((item) => ({ type, item })))

export const getConventionalTranslation = (
  languageMaps: LanguageMaps | undefined,
  type: ConventionalItemType,
  id: string,
  field: ConventionalTranslatedField
): ContentLanguageTranslation =>
  Object.fromEntries(conventionalLanguages.map((language) => [language, languageMaps?.[language]?.[conventionalTypeConfig[type].group]?.[id]?.[field] ?? '']))

export const toConventionalFormValues = (row: ConventionalItemRow, languageMaps: LanguageMaps | undefined): ConventionalItemFormValues => ({
  names: getConventionalTranslation(languageMaps, row.type, row.item.id, 'name'),
  descriptions: getConventionalTranslation(languageMaps, row.type, row.item.id, 'description'),
  imageTexts: getConventionalTranslation(languageMaps, row.type, row.item.id, 'imageText'),
  imageUrl: row.item.imageUrl ?? '',
  exampleAppIds: row.item.exampleAppIds ?? [],
})

const formFieldByTranslatedField: Record<ConventionalTranslatedField, 'names' | 'descriptions' | 'imageTexts'> = {
  name: 'names',
  description: 'descriptions',
  imageText: 'imageTexts',
}

export const toConventionalTranslations = (type: ConventionalItemType, values: ConventionalItemFormValues): ConventionalTranslations =>
  Object.fromEntries(
    conventionalLanguages.map((language) => [
      language,
      Object.fromEntries(conventionalTypeConfig[type].fields.map((field) => [field, values[formFieldByTranslatedField[field]][language] ?? ''])),
    ])
  )

export const toConventionalItemMutation = (row: ConventionalItemRow, values: ConventionalItemFormValues): ConventionalItemMutation => {
  const translations = toConventionalTranslations(row.type, values)
  const item: ConventionalItemMutation = { ...row.item, name: values.names.en ?? '', translations }
  if (row.type === ConventionalItemType.Category) return item
  item.description = values.descriptions.en ?? ''
  if (row.type === ConventionalItemType.Genre) return item
  return {
    ...item,
    imageText: values.imageTexts.en ?? '',
    imageUrl: values.imageUrl || (row.item.imageUrl === '' ? '' : null),
    exampleAppIds: values.exampleAppIds,
  }
}

export type ConventionalItemField = 'name' | 'description' | 'imageText' | 'imageUrl' | 'exampleAppIds' | 'translations'

export const conventionalFieldLabelKeys: Record<ConventionalItemField, string> = {
  name: 'internal-service-management:conventional_genres_name',
  description: 'internal-service-management:conventional_genres_description',
  imageText: 'internal-service-management:conventional_genres_image_text',
  imageUrl: 'internal-service-management:conventional_genres_image',
  exampleAppIds: 'internal-service-management:conventional_genres_example_games',
  translations: 'internal-service-management:conventional_genres_translations',
}

// The save answers 200 with an empty body, so the item and its translations are read back and compared with what was sent.
export const getUnstoredConventionalFields = (
  type: ConventionalItemType,
  sent: ConventionalItemMutation,
  saved: SavedConventionalItem
): ConventionalItemField[] => {
  const stored = saved.item
  if (!stored) return ['name']
  const unstored: ConventionalItemField[] = []
  if ((stored.name ?? '') !== sent.name) unstored.push('name')
  if (type !== ConventionalItemType.Category && (stored.description ?? '') !== (sent.description ?? '')) unstored.push('description')
  if (type === ConventionalItemType.Subgenre) {
    if ((stored.imageText ?? '') !== (sent.imageText ?? '')) unstored.push('imageText')
    if ((stored.imageUrl || null) !== (sent.imageUrl || null)) unstored.push('imageUrl')
    if ((stored.exampleAppIds ?? []).join() !== (sent.exampleAppIds ?? []).join()) unstored.push('exampleAppIds')
  }
  const translationsStored = conventionalLanguages.every((language) =>
    conventionalTypeConfig[type].fields.every((field) => (saved.translations[language]?.[field] ?? '') === (sent.translations[language]?.[field] ?? ''))
  )
  if (!translationsStored) unstored.push('translations')
  return unstored
}

// App ids that no game was found for are kept (legacy dropped them on save); the picked games follow, without duplicates.
export const mergeExampleAppIds = (current: number[], resolvedAppIds: Set<number>, pickedAppIds: number[], max: number): number[] =>
  Array.from(new Set([...current.filter((appId) => !resolvedAppIds.has(appId)), ...pickedAppIds])).slice(0, max)

export const readStoredTranslations = (languageMaps: LanguageMaps | undefined, type: ConventionalItemType, id: string): ConventionalTranslations =>
  Object.fromEntries(
    conventionalLanguages.map((language) => {
      const entry = languageMaps?.[language]?.[conventionalTypeConfig[type].group]?.[id] ?? {}
      return [language, Object.fromEntries(conventionalTypeConfig[type].fields.map((field) => [field, entry[field] ?? null]))]
    })
  )
